import React, { FC } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import Selector from 'modules/core/components/Selector'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import { useTaskWizard } from 'modules/processes/components/ItemWizard/Tasks/TaskProvider'
import { DataCollectionCategories } from 'modules/processes/domain'
import { useCustomFieldsOptions } from 'modules/processes/hooks/customFields/useCustomFieldsOptions'

interface Iconfiguration {
  field_ids: string[]
}

interface IConfig {
  category: string
  configuration: Iconfiguration
}

const CustomFieldSelector: FC = () => {
  const { setTaskConfiguration } = useTaskWizard()
  const { setPreviewValues } = usePreviewProvider()
  const { loading, customFields } = useCustomFieldsOptions([
    CustomField.UsageGroups.employeeQuestions,
  ])

  const { associatedTaskItem: item, form, setMultiselectOpened } = useFormProvider()

  const currentTaskConfiguration = form.get('task_type_configuration')?.value

  const [selectedFieldIds, setselectedFieldIds] = React.useState<string[]>([])

  const extractConfig = (taskTypeConfiguration?: string) => {
    if (!taskTypeConfiguration)
      return {
        category: DataCollectionCategories.employee_questions_cf,
        configuration: { field_ids: [] },
      }

    const { category, configuration }: IConfig =
      JSON.parse(taskTypeConfiguration).configuration_fields?.at(0)?.categories?.at(0) || {}

    return {
      category,
      configuration,
    }
  }

  // Remove this when the conflict between wizard and multiselect is fixed
  useEffect(() => {
    setMultiselectOpened(false)

    return () => {
      setMultiselectOpened(false)
    }
  }, [])

  useEffect(() => {
    const { configuration } = extractConfig(
      JSON.stringify(currentTaskConfiguration?.configuration_fields)
    )

    const hasSelectedFields = configuration?.field_ids.length > 0

    if (item && item.taskTypeConfiguration && !hasSelectedFields) {
      const { configuration: itemConfig } = extractConfig(item.taskTypeConfiguration)
      setselectedFieldIds(itemConfig?.field_ids || [])
    } else if (hasSelectedFields) {
      setselectedFieldIds(configuration?.field_ids)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const onCategoriesChange = (value: unknown) => {
    setselectedFieldIds(value as string[])
  }

  useUnsafeEffect(() => {
    setTaskConfiguration({
      configuration_fields: [
        {
          resource: 'employee',
          categories: [
            {
              category: DataCollectionCategories.employee_questions_cf,
              configuration: {
                field_ids: selectedFieldIds,
              },
            },
          ],
        },
      ],
    })

    setPreviewValues({
      selectedFields: selectedFieldIds.map((id) =>
        customFields.find((field) => field.value === id)
      ),
    })
  }, [selectedFieldIds.join(','), loading, customFields]) // eslint-disable-line react-hooks/exhaustive-deps

  const categoriesOptions = customFields.map((field) => {
    return (
      <Selector.Option
        key={field.value}
        value={field.value}
        label={field.label}
        searchValue={field.label}
      >
        <Box flexDirection="row" gap="s4" width="full" justifyContent="spaceBetween">
          <Text.Normal ellipsis weight="medium">
            {field.label}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const availableOptions = new Set(categoriesOptions.map((option) => String(option.key)))
  const filteredFieldIds = selectedFieldIds.filter((id) => availableOptions.has(id))

  return (
    <Selector.Popover
      label={i18n.t(
        'processes.process_settings.wizard.tasks.types.additional_employee_info.selector_field'
      )}
      placeholder={i18n.t('common.select')}
      blankLabel={i18n.t('common.select')}
      value={filteredFieldIds}
      onChange={onCategoriesChange}
      mandatory
      isMultiSelect
      hasSearch={false}
      footer={
        selectedFieldIds.length > 0 ? (
          <Box alignItems="center">
            <Button type="secondary" onClick={() => onCategoriesChange([])}>
              {i18n.t(
                'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.clear_selection'
              )}
            </Button>
          </Box>
        ) : undefined
      }
      onToggle={(isOpen) =>
        setTimeout(() => {
          setMultiselectOpened(isOpen)
        }, 25)
      }
    >
      {categoriesOptions}
    </Selector.Popover>
  )
}

export default CustomFieldSelector
